import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import type { PartnerStockAllocation } from '../../../models/PartnerPortalTypes';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { planStatusChipColor, planStatusLabel } from '../../../models/StockPickTypes';
import PartnerReadOnlyField from './PartnerReadOnlyField';
import PartnerChargesTable from './PartnerChargesTable';

interface Props {
  open: boolean;
  allocation: PartnerStockAllocation | null;
  onClose: () => void;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mt: 1 }}>
      {children}
    </Typography>
  );
}

const PartnerAllocationDetailDialog: React.FC<Props> = ({ open, allocation, onClose }) => {
  if (!allocation) return null;

  const exec = allocation.execution;
  const result = allocation.planResult;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {allocation.stockName}
        <Typography variant="caption" color="text.secondary" display="block">
          {allocation.nseCode}
          {allocation.bseCode ? ` / ${allocation.bseCode}` : ''}
          {allocation.planLabel ? ` · ${allocation.planLabel}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" icon={<Lock fontSize="small" />} sx={{ mb: 2 }}>
          Read-only view — contact your fund manager to update allocations or plan data.
        </Alert>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label={allocation.planCode} size="small" color="primary" />
          <Chip
            label={planStatusLabel(allocation.planStatus)}
            size="small"
            color={planStatusChipColor(allocation.planStatus)}
          />
          <Chip label={allocation.holdingTypeLabel} size="small" variant="outlined" />
          {allocation.domain && <Chip label={allocation.domain} size="small" variant="outlined" />}
          {allocation.capLabel && <Chip label={allocation.capLabel} size="small" variant="outlined" />}
        </Stack>

        <SectionTitle>Your allocation</SectionTitle>
        {allocation.purchaseTotalCost != null && allocation.purchaseTotalCost > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Your amount is your share of the plan purchase cost ({formatCurrency(allocation.purchaseTotalCost)}{' '}
            total including buy charges).
          </Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField
              label="Your share of purchase cost"
              value={formatCurrency(allocation.allocatedAmount)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField
              label="Share of plan capital"
              value={`${allocation.shareOfPlanPct.toFixed(1)}%`}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField label="Allocation date" value={allocation.allocationDate} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField
              label="Plan planned capital"
              value={
                allocation.plannedCapital != null ? formatCurrency(allocation.plannedCapital) : '—'
              }
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField
              label="Total capital on plan"
              value={formatCurrency(allocation.totalCapitalOnPlan)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <PartnerReadOnlyField label="Holding style" value={allocation.holdingTypeLabel} />
          </Grid>
        </Grid>

        {allocation.allocationNote && (
          <Box sx={{ mt: 2 }}>
            <PartnerReadOnlyField label="Note from fund manager" value={allocation.allocationNote} fullWidth />
          </Box>
        )}

        {allocation.planAndReasons && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Investment plan &amp; rationale</SectionTitle>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {allocation.planAndReasons}
            </Typography>
          </>
        )}

        {allocation.exitStrategy && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Exit strategy</SectionTitle>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {allocation.exitStrategy}
            </Typography>
          </>
        )}

        {allocation.riskMitigation && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Risk mitigation</SectionTitle>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {allocation.riskMitigation}
            </Typography>
          </>
        )}

        {(exec?.purchases?.length ?? 0) > 0 &&
          exec!.purchases.map((buy, idx) => (
            <React.Fragment key={`${buy.tradeDate}-${idx}`}>
              <Divider sx={{ my: 2 }} />
              <SectionTitle>
                Stock purchase {exec!.purchases.length > 1 ? `#${idx + 1}` : ''} (plan execution)
              </SectionTitle>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6} sm={3}>
                  <PartnerReadOnlyField label="Date" value={buy.tradeDate} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerReadOnlyField label="Shares bought" value={buy.quantity} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerReadOnlyField label="Price / share" value={formatCurrency(buy.pricePerShare)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PartnerReadOnlyField label="Gross value" value={formatCurrency(buy.grossAmount)} />
                </Grid>
              </Grid>
              {buy.comments && (
                <Box sx={{ mt: 1 }}>
                  <PartnerReadOnlyField label="Comments" value={buy.comments} fullWidth />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Purchase charges
              </Typography>
              <PartnerChargesTable charges={buy.charges} />
            </React.Fragment>
          ))}

        {exec && exec.sells.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Sells (partial exits)</SectionTitle>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Proceeds</TableCell>
                    <TableCell>Gain type</TableCell>
                    <TableCell align="right">Charges</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exec.sells.map((sell, i) => (
                    <TableRow key={`${sell.tradeDate}-${i}`}>
                      <TableCell>{sell.tradeDate}</TableCell>
                      <TableCell align="right">{sell.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(sell.pricePerShare)}</TableCell>
                      <TableCell align="right">{formatCurrency(sell.grossProceeds)}</TableCell>
                      <TableCell>{sell.capitalGainsType ?? '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(sell.totalCharges)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {exec && (exec.sharesBought > 0 || exec.sharesSold > 0) && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Position summary (plan level)</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField label="Shares bought" value={exec.sharesBought} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField label="Shares sold" value={exec.sharesSold} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField label="Shares remaining" value={exec.sharesRemaining} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField
                  label="Total purchase value"
                  value={formatCurrency(exec.totalPurchaseValue)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField
                  label="Total sell proceeds"
                  value={formatCurrency(exec.totalSellProceeds)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField
                  label="Total charges (buy + sell)"
                  value={formatCurrency(exec.totalCharges)}
                />
              </Grid>
            </Grid>
          </>
        )}

        {result && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>Plan result summary</SectionTitle>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Plan-level figures and your proportional share ({allocation.shareOfPlanPct.toFixed(1)}%).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <PartnerReadOnlyField label="Result date" value={result.resultDate ?? '—'} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PartnerReadOnlyField
                  label="Plan invested"
                  value={formatCurrency(result.investedAmount)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PartnerReadOnlyField label="Plan quantity" value={result.quantity} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PartnerReadOnlyField
                  label="Plan charges/tax"
                  value={formatCurrency(result.taxAmount)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField
                  label="Your invested share"
                  value={formatCurrency(result.yourInvestedShare)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <PartnerReadOnlyField
                  label="Your tax/charges share"
                  value={formatCurrency(result.yourTaxShare)}
                />
              </Grid>
            </Grid>
            {result.learning && (
              <Box sx={{ mt: 2 }}>
                <PartnerReadOnlyField label="Learning / notes" value={result.learning} fullWidth />
              </Box>
            )}
          </>
        )}

        {!(exec?.purchases?.length ?? 0) && !allocation.planAndReasons && !result && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No execution or plan narrative recorded yet for this allocation.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerAllocationDetailDialog;
